import { eventBus, EVENTS } from "../core/EventBus.js";

/**
 * @class UIManager
 * @description Gère tous les écrans du jeu (Start, Game Over, Win, Pause) et les écouteurs d'événements DOM.
 */
export class UIManager {
    constructor(hudManager) {
        this.hudManager = hudManager;

        // Éléments DOM des écrans
        this.startScreen    = document.getElementById("start-screen");
        this.gameOverScreen = document.getElementById("game-over-screen");
        this.gameWonScreen  = document.getElementById("game-won-screen");
        this.pauseMenu      = document.getElementById("pause-menu");
        this.storyScreen    = document.getElementById("story-screen");

        // Stats finales
        this.scoreEl = document.getElementById("final-score");
        this.timeEl  = document.getElementById("final-time");

        // Story slides state
        this._currentSlide = 0;
        this._totalSlides  = 4;

        this._initListeners();
        this._subscribeToEvents();
    }

    /**
     * Initialise les écouteurs d'événements pour les boutons de l'interface.
     * @private
     */
    _initListeners() {
        // --- BOUTONS DE RESTART ---
        const restartBtns = document.querySelectorAll("#restart-btn, #game-over-screen .blink");
        restartBtns.forEach(btn => {
            btn.addEventListener("click", () => {
                eventBus.emit(EVENTS.GAME_START);
            });
        });

        // --- PAUSE MENU CONTROLS ---
        const resumeBtn = document.getElementById("resume-btn");
        if (resumeBtn) {
            resumeBtn.addEventListener("click", () => {
                eventBus.emit("RESUME_REQUESTED");
            });
        }

        // Slider Volume
        const volumeSlider = document.getElementById("volume-slider");
        if (volumeSlider) {
            volumeSlider.addEventListener("input", (e) => {
                const vol = parseFloat(e.target.value);
                eventBus.emit("VOLUME_CHANGED", vol);
            });
        }

        // Layout Radio Buttons (Pause Menu)
        const pauseLayouts = document.querySelectorAll('input[name="pause-layout"]');
        pauseLayouts.forEach(radio => {
            radio.addEventListener("change", (e) => {
                if (e.target.checked) {
                    eventBus.emit("LAYOUT_CHANGED", e.target.value);
                }
            });
        });

        // Bouton Play sur la landing page → ouvre les slides
        const playBtn = document.getElementById("play-btn");
        if (playBtn) {
            playBtn.addEventListener("click", () => {
                this.showStorySlides();
            });
        }

        // Story slides navigation
        const storyNext = document.getElementById("story-next");
        const storySkip = document.getElementById("story-skip");
        if (storyNext) storyNext.addEventListener("click", () => this._storyAdvance());
        if (storySkip) storySkip.addEventListener("click", () => this._storyEnd());

        // Modal Équipe
        const teamBtn   = document.getElementById("team-btn");
        const teamModal = document.getElementById("team-modal");
        const teamClose = document.getElementById("team-modal-close");
        if (teamBtn && teamModal) {
            teamBtn.addEventListener("click", () => { teamModal.style.display = "flex"; });
            teamClose?.addEventListener("click", () => { teamModal.style.display = "none"; });
            teamModal.addEventListener("click", (e) => {
                if (e.target === teamModal) teamModal.style.display = "none";
            });
        }

        // Touche Enter : avance les slides si actifs, sinon démarre/redémarre
        window.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                if (this.storyScreen && this.storyScreen.classList.contains("active")) {
                    this._storyAdvance();
                } else {
                    eventBus.emit("ENTER_PRESSED");
                }
            }
        });
    }

    /**
     * S'abonne aux événements globaux via l'EventBus
     * @private
     */
    _subscribeToEvents() {
        eventBus.on(EVENTS.GAME_START, () => this.showGameScreen());
        eventBus.on(EVENTS.GAME_OVER, () => this.showGameOver());
        eventBus.on(EVENTS.GAME_WON, (data) => this.showGameWon(data));
        eventBus.on(EVENTS.GAME_PAUSE, () => this.showPauseMenu());
        eventBus.on(EVENTS.GAME_RESUME, () => this.hidePauseMenu());

        // Effets visuels
        eventBus.on(EVENTS.AI_ADAPTATION, () => {
            if (this.hudManager) this.hudManager.applyGlitchEffect();
        });
    }

    /**
     * Affiche l'écran de démarrage.
     */
    showStartScreen() {
        this._hideAllScreens();
        if (this.startScreen) this.startScreen.classList.add("active");
        if (this.hudManager) this.hudManager.hide();
    }

    /**
     * Affiche les slides narratifs d'intro.
     */
    showStorySlides() {
        this._hideAllScreens();
        this._currentSlide = 0;
        if (this.storyScreen) {
            this.storyScreen.classList.add("active");
            this._renderSlide(0);
        }
        if (this.hudManager) this.hudManager.hide();
    }

    /** @private */
    _renderSlide(index) {
        const slides = this.storyScreen.querySelectorAll(".story-slide");
        const dots   = this.storyScreen.querySelectorAll(".story-dot");
        const nextBtn = document.getElementById("story-next");

        slides.forEach((s, i) => s.classList.toggle("active", i === index));
        dots.forEach((d, i) => d.classList.toggle("active", i === index));

        if (nextBtn) {
            const isLast = index === this._totalSlides - 1;
            nextBtn.textContent = isLast ? "LANCER LA MISSION" : "SUIVANT →";
            nextBtn.classList.toggle("launch", isLast);
        }
    }

    /** @private */
    _storyAdvance() {
        if (this._currentSlide < this._totalSlides - 1) {
            this._currentSlide++;
            this._renderSlide(this._currentSlide);
        } else {
            this._storyEnd();
        }
    }

    /** @private */
    _storyEnd() {
        if (this.storyScreen) this.storyScreen.classList.remove("active");
        eventBus.emit("ENTER_PRESSED");
    }

    /**
     * Affiche l'interface de jeu (HUD) et masque les écrans.
     */
    showGameScreen() {
        this._hideAllScreens();
        if (this.hudManager) this.hudManager.show();
    }

    /**
     * Affiche l'écran de Game Over.
     */
    showGameOver() {
        if (this.gameOverScreen) this.gameOverScreen.classList.add("active");
        if (this.hudManager) this.hudManager.hide();
    }

    /**
     * Affiche l'écran de Victoire avec le score et le temps.
     * @param {Object} data - Données de fin de partie { score, time }
     */
    showGameWon(data = {}) {
        if (this.gameWonScreen) this.gameWonScreen.classList.add("active");
        if (this.hudManager) this.hudManager.hide();

        if (this.scoreEl) this.scoreEl.innerText = data.score || `${Math.floor(Math.random() * 20 + 80)}%`;
        if (this.timeEl) this.timeEl.innerText = data.time || "00:00";
    }

    /**
     * Affiche le menu de pause.
     */
    showPauseMenu() {
        if (this.pauseMenu) this.pauseMenu.classList.add("active");
    }

    /**
     * Masque le menu de pause.
     */
    hidePauseMenu() {
        if (this.pauseMenu) this.pauseMenu.classList.remove("active");
    }

    /**
     * Masque tous les écrans (Accueil, Game Over, Victoire, Pause).
     * @private
     */
    _hideAllScreens() {
        if (this.startScreen)    this.startScreen.classList.remove("active");
        if (this.gameOverScreen) this.gameOverScreen.classList.remove("active");
        if (this.gameWonScreen)  this.gameWonScreen.classList.remove("active");
        if (this.pauseMenu)      this.pauseMenu.classList.remove("active");
        if (this.storyScreen)    this.storyScreen.classList.remove("active");
    }

    /**
     * Met à jour le HUD
     * @param {Object} data - Données du HUD
     */
    updateHUD(data) {
        if (this.hudManager) {
            this.hudManager.update(data);
        }
    }
}
